import { forwardRef, type ButtonHTMLAttributes, type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * TabButton — segmented-control / pill-tab style button with an active state.
 *
 * Wrap a row of TabButtons in `<TabGroup>` to get the inset pill-track background.
 * The container is purely visual; a11y semantics (role="tablist"/"tabpanel" vs
 * role="radiogroup") are left to the consumer because the right answer depends
 * on whether selecting a tab swaps a panel (tabs) or sets a value (radio).
 */
export const tabButtonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 font-medium transition-colors ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-ink-500/40 ' +
    'disabled:pointer-events-none disabled:opacity-60',
  {
    variants: {
      size: {
        sm: 'px-2.5 py-1 text-xs rounded-md',
        md: 'px-3 py-1.5 text-sm rounded-md',
        lg: 'px-4 py-2 text-sm rounded-lg',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

const ACTIVE = 'bg-white text-ink-900 shadow-sm';
const INACTIVE = 'text-ink-600 hover:bg-white/60 hover:text-ink-900';

export interface TabButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof tabButtonVariants> {
  active: boolean;
}

export const TabButton = forwardRef<HTMLButtonElement, TabButtonProps>(
  ({ className, size, active, type = 'button', children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        type={type}
        aria-pressed={active}
        className={cn(tabButtonVariants({ size }), active ? ACTIVE : INACTIVE, className)}
        {...props}
      >
        {children}
      </button>
    );
  }
);
TabButton.displayName = 'TabButton';

export type TabGroupProps = HTMLAttributes<HTMLDivElement>;

export const TabGroup = forwardRef<HTMLDivElement, TabGroupProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('inline-flex items-center gap-1 rounded-lg bg-ink-100 p-1', className)}
      {...props}
    />
  )
);
TabGroup.displayName = 'TabGroup';
